/**
 * ONE poller per app-wide feed (defect fix 2026-08-29)
 * ====================================================
 *
 * Measured on /today: 58 API calls for 29 distinct endpoints in a single load.
 * Half of that doubling is React StrictMode and is dev-only. The other half
 * was structural — three hooks that each open a `setInterval` were mounted by
 * several live components at once, so the same endpoint was polled N times a
 * minute for one screen's worth of information:
 *
 *   useCoachAlerts            App shell + TodayFocus (both live at once)
 *   useServerTime             Sidebar (via useTimeOfDay) + TodayFocus, and on
 *                             other routes ScheduleV2 / ReviewSchedule
 *   useMotivatorNotifications Header or MobileLayout — mutually exclusive on a
 *                             given viewport, so this one was NOT two live
 *                             pollers; hoisting it stops the feed being thrown
 *                             away and refetched when the viewport crosses the
 *                             mobile breakpoint, and keeps the three feeds
 *                             owned in one place.
 *
 * The fix is placement, not policy: each feed is fetched once here, at the app
 * root, and published through a context that the hooks read. Every call site
 * keeps its exact shape — `useCoachAlerts()` and friends still return the same
 * object; they just stop each opening a poller of their own to get it.
 *
 * This lives INSIDE the Router: useCoachAlerts refetches on route change.
 *
 * A component that genuinely needs a different request still gets its own —
 * `useServerTime({ timezone })` asks a different URL and is left alone.
 */

import type { ReactNode } from 'react'
import { CoachAlertsContext, useCoachAlertsSource } from '../hooks/useCoachAlerts'
import {
  MotivatorNotificationsContext,
  useMotivatorNotificationsSource,
} from '../hooks/useMotivatorNotifications'
import { ServerTimeContext, useServerTimeSource } from '../hooks/useServerTime'

export function SharedPollingProvider({ children }: { children: ReactNode }) {
  const serverTime = useServerTimeSource()
  const coachAlerts = useCoachAlertsSource()
  const notifications = useMotivatorNotificationsSource()

  return (
    <ServerTimeContext.Provider value={serverTime}>
      <CoachAlertsContext.Provider value={coachAlerts}>
        <MotivatorNotificationsContext.Provider value={notifications}>
          {children}
        </MotivatorNotificationsContext.Provider>
      </CoachAlertsContext.Provider>
    </ServerTimeContext.Provider>
  )
}
